<script setup lang="ts">
  import { useReportsStore } from '../../store';
  import { useDisplay } from 'vuetify';
  import { computed, watch, onMounted } from 'vue';
  import { useRouter, useRoute } from 'vue-router';

  const props = defineProps<{
    total: number;
  }>();

  const reportsStore = useReportsStore();
  const display = useDisplay();
  const router = useRouter();
  const route = useRoute();
  const mdAndUp = computed(() => display.mdAndUp.value);

  const emit = defineEmits<{
    (e: 'sort-change', option: string): void;
  }>();

  // Map sort options to URL parameters
  const sortOptionMap = {
    'Recently Published': { order: 'published', dir: 'desc' },
    Alphabetical: { order: 'alpha', dir: 'asc' },
    Copyright: { order: 'edition', dir: 'asc' },
  };

  // Function to update URL with sort parameters
  const updateSortUrl = (option: string) => {
    const sortParams = sortOptionMap[option as keyof typeof sortOptionMap];
    const query = { ...route.query };

    // Remove sort params if they're the default values
    if (option === 'Recently Published') {
      delete query.order;
      delete query.dir;
    } else {
      Object.assign(query, sortParams);
    }

    router.push({
      path: route.path,
      query,
      replace: true,
    });
  };

  // Watch for URL changes to update sort option
  watch(
    () => route.query,
    (newQuery) => {
      const { order, dir } = newQuery;

      // If no sort parameters, set to default
      if (!order) {
        reportsStore.setSortOption('Recently Published');
        return;
      }

      // Find matching sort option
      const option = Object.entries(sortOptionMap).find(
        ([_, value]) => value.order === order && value.dir === dir,
      )?.[0];

      if (option) {
        reportsStore.setSortOption(option);
      } else {
        // If no matching option found, set to default
        reportsStore.setSortOption('Recently Published');
      }
    },
    { immediate: true },
  );

  // Computed property for the v-select model
  const sortOption = computed({
    get: () => {
      const { order, dir } = route.query;
      if (!order) return 'Recently Published';

      const option = Object.entries(sortOptionMap).find(
        ([_, value]) => value.order === order && value.dir === dir,
      )?.[0];

      return option || 'Recently Published';
    },
    set: (value: string) => {
      reportsStore.setSortOption(value);
      updateSortUrl(value);
      emit('sort-change', value);
    },
  });

  // Initialize sort option from URL on component mount
  onMounted(() => {
    const { order, dir } = route.query;
    if (order && dir) {
      const option = Object.entries(sortOptionMap).find(
        ([_, value]) => value.order === order && value.dir === dir,
      )?.[0];
      if (option) {
        reportsStore.setSortOption(option);
      }
    }
  });
</script>

<template>
  <v-container
    class="d-flex justify-center pa-0"
    :class="{ 'mb-8': mdAndUp }"
    style="max-width: 1320px"
  >
    <v-responsive class="w-100">
      <v-row v-show="!mdAndUp">
        <v-col cols="12" md="2">
          <div class="d-flex w-100 search-container mobile-search">
            <v-text-field
              density="compact"
              variant="outlined"
              placeholder="Search"
              hide-details
              class="search-field"
            ></v-text-field>
            <v-btn
              icon
              variant="text"
              color="primary"
              class="search-btn"
              size="small"
            >
              <v-icon size="20" color="white">mdi-magnify</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <v-row class="mt-6 align-center">
        <v-col cols="12" md="2">
          <h4 class="text-secondary font-weight-bold text-right text-md-left">
            Showing {{ props.total }} Results
          </h4>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="[
            mdAndUp ? 'd-flex flex-row align-center justify-start' : 'd-none',
          ]"
        >
          <div v-show="mdAndUp" style="width: 280px">
            <div class="d-flex search-container">
              <v-text-field
                density="compact"
                variant="outlined"
                placeholder="Search"
                hide-details
                class="search-field"
              ></v-text-field>
              <v-btn
                icon
                variant="text"
                color="primary"
                class="search-btn"
                size="small"
              >
                <v-icon size="20" color="white">mdi-magnify</v-icon>
              </v-btn>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="4" :class="[mdAndUp ? '' : 'd-none']">
          <div
            class="sort-by-container d-flex align-center"
            :class="mdAndUp ? 'justify-end' : 'justify-start'"
          >
            <h5 class="text-secondary font-weight-bold">Sort by:</h5>
            <v-select
              v-model="sortOption"
              :items="['Recently Published', 'Alphabetical', 'Copyright']"
              variant="outlined"
              :hide-details="true"
              density="compact"
              class="small-select pl-2"
              append-inner-icon="mdi-chevron-down"
              :menu-props="{
                contentClass: 'select-menu-content',
                transition: 'scale-transition',
                location: 'bottom',
              }"
            />
          </div>
        </v-col>
      </v-row>
    </v-responsive>
  </v-container>
</template>

<style scoped lang="scss">
  // Variables
  $button-size: 32px;
  $search-max-width: 218px;

  .search-container {
    flex: 1;
    position: relative;
    max-width: $search-max-width;

    &.mobile-search {
      max-width: none;
    }

    &[v-show='false'] {
      display: none !important;
    }
  }

  :deep(.search-field) {
    .v-field {
      background-color: white !important;
      border-end-end-radius: 0 !important;
      border-start-end-radius: 0 !important;
      height: $button-size !important;
      min-height: $button-size !important;
      display: flex !important;
      align-items: center !important;

      &:hover {
        border-color: #cccccc !important;
      }
    }

    .v-field__outline {
      display: none !important;
    }

    .v-field__field {
      border: 1px solid #cccccc !important;
      border-radius: 4px 0 0 4px !important;
      height: $button-size !important;
      min-height: $button-size !important;
      display: flex !important;
      align-items: center !important;

      &:before,
      &:after {
        display: none !important;
      }

      &:hover {
        border-color: #cccccc !important;
      }
    }

    .v-field--focused .v-field__field {
      border-color: rgb(var(--v-theme-primary)) !important;
    }

    .v-field__input {
      color: rgb(var(--v-theme-tonal-black)) !important;
      font-size: 12px !important;
      padding: 4px 10px !important;
      min-height: $button-size !important;
      height: $button-size !important;
      display: flex !important;
      align-items: center !important;
    }
  }

  :deep(.select-menu-content .v-list) {
    // display: none; // This seems like it would hide the dropdown? Check if needed.
  }

  // Search button styles
  :deep(.search-btn) {
    background-color: rgb(var(--v-theme-primary)) !important;
    margin: 0 !important;
    height: 32px !important;
    width: $button-size !important;
    min-width: $button-size !important;
    border-radius: 0 4px 4px 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    line-height: 1 !important;

    .v-btn__content {
      padding: 0 !important;
      margin: 0 !important;
      width: 20px !important;
      height: 20px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }

    .v-icon {
      margin-left: 0 !important;
      margin-right: 0 !important;
      padding: 0 !important;
      width: 20px !important;
      height: 20px !important;
    }
  }
</style>
